import React, { useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const OrganizationsTable = ({
  organizations,
  onRowClick,
  onSort,
  onPaginationChanged,
  paginationState,
}) => {
  const { t } = useTranslation(["auth"]);
  const gridRef = useRef();

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Name"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "shortName",
        headerName: t("OrganizationShortName"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        headerClass: "ag-center-aligned-header",
        flex: 1,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 100,
    }),
    []
  );

  // Handle sort changed event
  const handleSortChanged = useCallback(
    (event) => {
      const { api } = event;
      const columnState = api.getColumnState();

      if (columnState && columnState.length > 0) {
        const sortedColumn = columnState.find((col) => col.sort);
        if (sortedColumn && onSort) {
          onSort(sortedColumn.colId, sortedColumn.sort);
        }
      }
    },
    [onSort]
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Use page size from props with fallback
      pageSize: paginationState?.pageSize || 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback(
    (params) => {
      if (onPaginationChanged && params) {
        onPaginationChanged(params);
      }
    },
    [onPaginationChanged]
  );

  // Handle grid ready event
  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params;

      // Set current page from external state if available
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={organizations}
      onSortChanged={handleSortChanged}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      onPaginationChanged={handlePaginationChanged}
      onGridReady={onGridReady}
      getRowId={(params) => params.data.organizationId.toString()}
      animateRows={true}
      // Normal layout for pagination
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      paginationPageSize={paginationConfig.pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      suppressPaginationPanel={false}
    />
  );
};

export default React.memo(OrganizationsTable);

